import { computed, ref } from 'vue'
import type { Ref } from 'vue'
import type { AddEntityType } from '~/components/orga/OrgaElement.vue'
import type { GridCol } from '~~/types/orgGrid'

export const OVERLAY_EQUIPE_DOMAINE_TITLE = 'Ajouter CODIR'

type UseDirectionPageOverlaysParams = {
  directionLib: Ref<string>
  grid: Ref<GridCol[]>
}

export function useDirectionPageOverlays(params: UseDirectionPageOverlaysParams) {
  const route = useRoute()

  const showOverlayDomaine = ref(false)
  const showOverlayModifyDomaine = ref(false)
  const modifyDomaineContext = ref<{ directionLib: string; domaineLib: string }>({ directionLib: '', domaineLib: '' })
  const showOverlayAgence = ref(false)
  const defaultDomaineForAgence = ref<string>('')
  const showOverlayEquipe = ref(false)
  const showOverlayModifyEquipeDomaine = ref(false)
  const showOverlayModifyEquipeAgence = ref(false)
  const showOverlayModifyAgence = ref(false)
  const modifyEquipeDomaineContext = ref<{ directionLib: string; domaineLib: string }>({ directionLib: '', domaineLib: '' })
  const modifyEquipeAgenceContext = ref<{ directionLib: string; domaineLib: string; ageLib: string }>({ directionLib: '', domaineLib: '', ageLib: '' })
  const modifyAgenceContext = ref<{ directionLib: string; domaineLib: string; ageLib: string }>({ directionLib: '', domaineLib: '', ageLib: '' })
  const equipeFormContext = ref<{
    directionName: string
    domaineName: string
    agenceName: string
    defaultEquLib?: string
  }>({ directionName: '', domaineName: '', agenceName: '' })

  const goToHrefModifyAgence = computed(() => (route.fullPath || undefined))
  const goToHrefModifyEquipeDomaine = computed(() => {
    const { directionLib, domaineLib } = modifyEquipeDomaineContext.value
    if (!directionLib || !domaineLib) return undefined
    const dir = route.params.direction as string
    return `/${dir}/${encodeURIComponent(domaineLib)}/${encodeURIComponent(domaineLib)}/${encodeURIComponent(domaineLib)}`
  })
  const goToHrefModifyEquipeAgence = computed(() => {
    const { directionLib, domaineLib, ageLib } = modifyEquipeAgenceContext.value
    if (!directionLib || !domaineLib || !ageLib) return undefined
    const dir = route.params.direction as string
    return `/${dir}/${encodeURIComponent(domaineLib)}/${encodeURIComponent(ageLib)}/${encodeURIComponent(ageLib)}`
  })

  const domainesForForm = computed(() =>
    params.grid.value
      .map(col => col?.header?.label)
      .filter((label): label is string => !!label)
      .map(domLib => ({ domLib, domLibLong: null as string | null, domDesc: null as string | null }))
  )

  const domainesWithAgencesForForm = computed(() => {
    const dir = params.directionLib.value
    const acc: Record<string, { dirLib: string; domLib: string; ageLib: string; ageLibLong?: string | null; ageDesc?: string | null }[]> = {}
    params.grid.value.forEach(col => {
      const domLib = col?.header?.label
      if (!domLib) return
      const key = `${dir}~${domLib}`
      acc[key] = (col.items ?? []).map(item => ({
        dirLib: dir,
        domLib,
        ageLib: item?.label ?? '',
        ageLibLong: item?.libLong ?? null,
        ageDesc: item?.desc ?? null
      }))
    })
    return acc
  })

  function onAddClick(payload: { entityType: AddEntityType; context?: Record<string, string | undefined> }) {
    if (payload.entityType === 'domaine') {
      showOverlayDomaine.value = true
    } else if (payload.entityType === 'agence') {
      defaultDomaineForAgence.value = payload.context?.domaineLib ?? ''
      showOverlayAgence.value = true
    } else if (payload.entityType === 'equipeDomaine') {
      const ctx = payload.context
      const dir = ctx?.directionLib ?? params.directionLib.value
      const dom = ctx?.domaineLib ?? ''
      equipeFormContext.value = { directionName: dir, domaineName: dom, agenceName: dom, defaultEquLib: dom }
      showOverlayEquipe.value = true
    }
  }

  function onModifyClick(payload?: Record<string, string | undefined>) {
    if (payload?.modifyKind === 'agence') {
      modifyAgenceContext.value = {
        directionLib: payload?.directionLib ?? params.directionLib.value,
        domaineLib: payload?.domaineLib ?? '',
        ageLib: payload?.ageLib ?? ''
      }
      showOverlayModifyAgence.value = true
      return
    }
    if (payload?.modifyKind === 'domaine') {
      modifyDomaineContext.value = {
        directionLib: payload?.directionLib ?? params.directionLib.value,
        domaineLib: payload?.domaineLib ?? ''
      }
      showOverlayModifyDomaine.value = true
      return
    }
    if (payload?.modifyKind !== 'equipe') return
    const dom = payload?.domaineLib
    const age = payload?.ageLib
    if (age !== undefined && age !== '') {
      modifyEquipeAgenceContext.value = {
        directionLib: payload?.directionLib ?? params.directionLib.value,
        domaineLib: dom ?? '',
        ageLib: age
      }
      showOverlayModifyEquipeAgence.value = true
    } else if (dom !== undefined) {
      modifyEquipeDomaineContext.value = {
        directionLib: payload?.directionLib ?? params.directionLib.value,
        domaineLib: dom
      }
      showOverlayModifyEquipeDomaine.value = true
    }
  }

  function closeOverlayModifyEquipeDomaine() {
    showOverlayModifyEquipeDomaine.value = false
    modifyEquipeDomaineContext.value = { directionLib: '', domaineLib: '' }
  }

  function closeOverlayModifyEquipeAgence() {
    showOverlayModifyEquipeAgence.value = false
    modifyEquipeAgenceContext.value = { directionLib: '', domaineLib: '', ageLib: '' }
  }

  function closeOverlayModifyAgence() {
    showOverlayModifyAgence.value = false
    modifyAgenceContext.value = { directionLib: '', domaineLib: '', ageLib: '' }
  }

  function closeOverlayDomaine() {
    showOverlayDomaine.value = false
  }

  function closeOverlayModifyDomaine() {
    showOverlayModifyDomaine.value = false
    modifyDomaineContext.value = { directionLib: '', domaineLib: '' }
  }

  function closeOverlayAgence() {
    showOverlayAgence.value = false
    defaultDomaineForAgence.value = ''
  }

  function closeOverlayEquipe() {
    showOverlayEquipe.value = false
  }

  return {
    showOverlayDomaine,
    showOverlayModifyDomaine,
    modifyDomaineContext,
    showOverlayAgence,
    defaultDomaineForAgence,
    showOverlayEquipe,
    showOverlayModifyEquipeDomaine,
    showOverlayModifyEquipeAgence,
    showOverlayModifyAgence,
    modifyEquipeDomaineContext,
    modifyEquipeAgenceContext,
    modifyAgenceContext,
    equipeFormContext,
    goToHrefModifyAgence,
    goToHrefModifyEquipeDomaine,
    goToHrefModifyEquipeAgence,
    domainesForForm,
    domainesWithAgencesForForm,
    onAddClick,
    onModifyClick,
    closeOverlayModifyEquipeDomaine,
    closeOverlayModifyEquipeAgence,
    closeOverlayModifyAgence,
    closeOverlayDomaine,
    closeOverlayModifyDomaine,
    closeOverlayAgence,
    closeOverlayEquipe,
  }
}
